import { useState, useEffect, useCallback } from "react";
import { fetchWords } from "../api/darija";

const shuffleArray = (arr) => [...arr].sort(() => Math.random() - 0.5);

/**
 * useQuiz — centralises all quiz state and logic.
 *
 * @param {string} endpoint   - API endpoint, e.g. "adjectives"
 * @param {string} answerKey  - key on each word object that holds the correct answer, e.g. "darija" or "darijaBase"
 *
 * Returns an object with:
 *   data, order, currentIndex, input, showAnswer, messages,
 *   progress, quizFinished, loading, error,
 *   currentWord, setInput,
 *   handleSubmit, handleShowAnswer, nextWord, resetQuiz,
 *   addMessage, handleCloseMessage
 */
export function useQuiz(endpoint, answerKey = "darija") {
    const [data, setData] = useState([]);
    const [order, setOrder] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [input, setInput] = useState("");
    const [showAnswer, setShowAnswer] = useState(false);
    const [messages, setMessages] = useState([]);
    const [progress, setProgress] = useState([]);
    const [quizFinished, setQuizFinished] = useState(false);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch data on mount
    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);

        fetchWords(endpoint)
            .then((words) => {
                if (cancelled) return;
                const shuffled = shuffleArray(words.map((_, i) => i));
                setData(words);
                setOrder(shuffled);
                setProgress(shuffled.map(() => ({ correct: false, revealed: false })));
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`[useQuiz] ${endpoint}:`, err);
                setError(err.message || "Ophalen mislukt.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [endpoint]);

    const currentWord = order.length ? data[order[currentIndex]] : null;

    const addMessage = useCallback(
        (type, text) => setMessages((prev) => [...prev, { type, text, id: Date.now() }]),
        []
    );

    const handleCloseMessage = useCallback(
        (id) => setMessages((prev) => prev.filter((m) => m.id !== id)),
        []
    );

    const nextWord = useCallback(() => {
        if (currentIndex + 1 < order.length) {
            setCurrentIndex((i) => i + 1);
            setInput("");
            setShowAnswer(false);
        } else {
            setQuizFinished(true);
        }
    }, [currentIndex, order.length]);

    const handleSubmit = useCallback(() => {
        if (!currentWord) return;

        if (progress[currentIndex]?.revealed) {
            addMessage("error", "❌ Antwoord laten zien telt als fout.");
            setTimeout(nextWord, 400);
            return;
        }

        const correct =
            input.trim().toLowerCase() === currentWord[answerKey]?.toLowerCase();

        if (correct) {
            addMessage("success", "✅ Goed gedaan!");
            setProgress((prev) => {
                const next = [...prev];
                next[currentIndex] = { ...next[currentIndex], correct: true };
                return next;
            });
            setTimeout(nextWord, 400);
        } else {
            addMessage("error", "❌ Fout! Probeer opnieuw.");
        }
    }, [currentWord, progress, currentIndex, input, answerKey, addMessage, nextWord]);

    const handleShowAnswer = useCallback(() => {
        setShowAnswer(true);
        setProgress((prev) => {
            const next = [...prev];
            next[currentIndex] = { correct: false, revealed: true };
            return next;
        });
    }, [currentIndex]);

    const resetQuiz = useCallback(() => {
        const shuffled = shuffleArray(data.map((_, i) => i));
        setOrder(shuffled);
        setProgress(shuffled.map(() => ({ correct: false, revealed: false })));
        setCurrentIndex(0);
        setInput("");
        setShowAnswer(false);
        setQuizFinished(false);
        setMessages([]);
    }, [data]);

    // Derived values for the end screen
    const correctCount = progress.filter((p) => p.correct).length;
    const wrongItems = progress
        .map((p, i) => ({ ...p, word: data[order[i]] }))
        .filter((p) => !p.correct)
        .map((w) => ({ question: w.word?.dutch, answer: w.word?.[answerKey] }));

    return {
        data,
        order,
        currentIndex,
        input,
        setInput,
        showAnswer,
        messages,
        progress,
        quizFinished,
        loading,
        error,
        currentWord,
        handleSubmit,
        handleShowAnswer,
        nextWord,
        resetQuiz,
        addMessage,
        handleCloseMessage,
        correctCount,
        wrongItems,
    };
}

/**
 * useMultipleChoiceQuiz — quiz state for multiple-choice questions.
 *
 * @param {string} endpoint
 * @param {string} answerKey
 * @param {number} choiceCount  - total number of options (default 4)
 */
export function useMultipleChoiceQuiz(endpoint, answerKey = "darija", choiceCount = 4) {
    const [allData, setAllData] = useState([]);
    const [order, setOrder] = useState([]);
    const [currentIndex, setCurrentIndex] = useState(0);
    const [options, setOptions] = useState([]);
    const [selected, setSelected] = useState(null);
    const [quizFinished, setQuizFinished] = useState(false);
    const [score, setScore] = useState(0);
    const [wrong, setWrong] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch on mount
    useEffect(() => {
        let cancelled = false;

        setLoading(true);
        setError(null);

        fetchWords(endpoint)
            .then((words) => {
                if (cancelled) return;
                const shuffled = shuffleArray(words.map((_, i) => i));
                setAllData(words);
                setOrder(shuffled);
                buildOptions(words, shuffled, 0, answerKey, choiceCount, setOptions);
            })
            .catch((err) => {
                if (cancelled) return;
                console.error(`[useMultipleChoiceQuiz] ${endpoint}:`, err);
                setError(err.message || "Ophalen mislukt.");
            })
            .finally(() => {
                if (!cancelled) setLoading(false);
            });

        return () => { cancelled = true; };
    }, [endpoint, answerKey, choiceCount]);

    const currentWord = order.length ? allData[order[currentIndex]] : null;

    const handleAnswer = useCallback(
        (option) => {
            if (selected || !currentWord) return;
            setSelected(option);

            const isCorrect = option === currentWord[answerKey]?.toLowerCase();

            if (isCorrect) {
                setScore((s) => s + 1);
            } else {
                setWrong((w) => [
                    ...w,
                    { question: currentWord.dutch, answer: currentWord[answerKey] },
                ]);
            }

            setTimeout(() => {
                const nextIndex = currentIndex + 1;
                if (nextIndex < order.length) {
                    setCurrentIndex(nextIndex);
                    setSelected(null);
                    buildOptions(allData, order, nextIndex, answerKey, choiceCount, setOptions);
                } else {
                    setQuizFinished(true);
                }
            }, 1200);
        },
        [selected, currentWord, currentIndex, order, allData, answerKey, choiceCount]
    );

    const resetQuiz = useCallback(() => {
        const shuffled = shuffleArray(allData.map((_, i) => i));
        setOrder(shuffled);
        setCurrentIndex(0);
        setSelected(null);
        setQuizFinished(false);
        setScore(0);
        setWrong([]);
        buildOptions(allData, shuffled, 0, answerKey, choiceCount, setOptions);
    }, [allData, answerKey, choiceCount]);

    return {
        allData,
        order,
        currentIndex,
        options,
        selected,
        quizFinished,
        score,
        wrong,
        loading,
        error,
        currentWord,
        handleAnswer,
        resetQuiz,
    };
}

// ─── Internal helper ──────────────────────────────────────────────────────────

function buildOptions(data, order, index, answerKey, count, setOptions) {
    if (!data.length || !order.length) return;

    const current = data[order[index]];
    const correct = current[answerKey]?.toLowerCase();
    const distractors = shuffleArray(
        data.filter((_, i) => i !== order[index])
    )
        .slice(0, count - 1)
        .map((w) => w[answerKey]?.toLowerCase());

    setOptions(shuffleArray([correct, ...distractors]));
}
